import { useMemo, type FC } from 'react';
import { vegaCopyConversation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { DateFormat } from '@gonfalon/format/constants';
import { format } from '@gonfalon/format/format';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { Text } from '@gonfalon/observability-ui/components/Text';

import type { ResourceOrQuery } from '../../../../resources';
import { trackVegaAiMessageCopied } from '../../../../vegaAnalytics';
import { parseMessageMentions } from '../../../mentions';
import { CopyButton } from '../CopyButton';

import { ContextPills } from './ContextPills';
import { InlineContextMessage } from './InlineContextMessage';
import { MessageWrapper } from './MessageWrapper';

import { copyAffordance, copyHoverGroup } from '../CopyButton.css';

// Component for rendering user messages
export const UserMessage: FC<{
  message: string;
  context?: ResourceOrQuery[];
  timestamp?: string;
}> = ({ message, context, timestamp }) => {
  const copyEnabled = vegaCopyConversation();

  const formattedTimestamp = timestamp ? format(timestamp, DateFormat.MMM_D_H_MM_A) : null;

  const externalContext = useMemo(() => {
    if (!context?.length) {
      return undefined;
    }
    const inlineMentionIds = new Set(
      parseMessageMentions(message).flatMap((s) => (s.type === 'mention' && s.mention ? [s.mention.id] : [])),
    );
    return context.filter((r) => !inlineMentionIds.has(r.id));
  }, [message, context]);

  return (
    <Box marginBottom="12" cssClass={copyEnabled ? copyHoverGroup : undefined}>
      <MessageWrapper>
        {externalContext && externalContext.length > 0 && (
          <Box marginBottom="8">
            <ContextPills context={externalContext} />
          </Box>
        )}

        <Box style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
          <InlineContextMessage message={message} />
        </Box>
      </MessageWrapper>

      {copyEnabled && (
        <Box
          marginTop="2"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap="4"
          cssClass={copyAffordance}
        >
          {formattedTimestamp && (
            <Text size="xSmall" color="weak">
              {formattedTimestamp}
            </Text>
          )}
          <CopyButton
            text={message}
            label="Copy message"
            successTitle="Message copied to clipboard"
            onCopy={() => trackVegaAiMessageCopied('user')}
          />
        </Box>
      )}
    </Box>
  );
};
